'use client'

import { useQuery } from '@tanstack/react-query'
import {
  Check,
  GraduationCap,
  type LucideIcon,
  MonitorPlay,
  Route,
  Video,
  X,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  areaQueryOptions,
  areasQueryOptions,
  categorySlugsQueryOptions,
} from '@/features/areas/queries'
import { useCatalogFilters } from '@/features/products-catalog/hooks/use-catalog-filters'
import {
  DEFAULT_PRODUCT_TYPE,
  type FilterableProductType,
  isProductTypeSlug,
} from '@/helpers/products-filter-type'
import { slugTransform } from '@/helpers/slug-transform'
import { cn } from '@/lib/utils'

export interface ContentTypeOption {
  value: FilterableProductType
  label: string
  icon: LucideIcon
}

export const CONTENT_TYPES: ContentTypeOption[] = [
  { value: 'course', label: 'Cursos Online', icon: MonitorPlay },
  { value: 'postgraduate', label: 'Pós-Graduação', icon: GraduationCap },
  { value: 'track', label: 'Trilhas', icon: Route },
  { value: 'webinar', label: 'Webinar', icon: Video },
]

export function getContentTypes({
  hasTrack,
  hasPostgraduate,
}: {
  hasTrack: boolean
  hasPostgraduate: boolean
}) {
  return CONTENT_TYPES.filter(
    ({ value }) =>
      (value !== 'track' || hasTrack) &&
      (value !== 'postgraduate' || hasPostgraduate)
  )
}

const SECTION = {
  areas: 'areas',
  categories: 'categorias',
  workload: 'carga-horaria',
} as const

const ALL_SECTIONS: string[] = Object.values(SECTION)

function formatWorkload(workload: string) {
  return `${workload}h`
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <h3 className="font-medium text-gray-400 text-sm">{children}</h3>
}

interface ContentTypeListProps {
  options: ContentTypeOption[]
  selected: FilterableProductType
  onSelect: (value: FilterableProductType) => void
}

function ContentTypeList({
  options,
  selected,
  onSelect,
}: ContentTypeListProps) {
  return (
    <div className="mt-2 grid grid-cols-2 gap-2">
      {options.map(({ value, label, icon: Icon }) => {
        const isSelected = value === selected
        return (
          <button
            aria-pressed={isSelected}
            className={cn(
              'flex cursor-pointer flex-col items-start gap-2 rounded-xl border px-3 py-2.5 text-left font-medium text-sm transition-all',
              isSelected
                ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-sm'
                : 'border-gray-200 text-gray-700 hover:border-primary-300 hover:bg-gray-50'
            )}
            key={value}
            onClick={() => onSelect(value)}
            type="button"
          >
            <Icon
              className={cn(
                'size-4',
                isSelected ? 'text-primary-600' : 'text-gray-400'
              )}
            />
            {label}
          </button>
        )
      })}
    </div>
  )
}

interface FilterTriggerProps {
  title: string
  value?: string
  placeholder?: string
}

function FilterTrigger({ title, value, placeholder }: FilterTriggerProps) {
  const hint = value ?? placeholder

  return (
    <AccordionTrigger className="cursor-pointer gap-3 py-3.5 text-base text-gray-800 hover:no-underline disabled:cursor-not-allowed [&>svg]:text-gray-400">
      <span className="flex min-w-0 flex-1 items-baseline justify-between gap-3">
        <span className="font-medium">{title}</span>
        {hint && (
          <span
            className={cn(
              'truncate text-sm',
              value ? 'font-semibold text-primary-700' : 'text-gray-400'
            )}
          >
            {hint}
          </span>
        )}
      </span>
    </AccordionTrigger>
  )
}

interface Option {
  key: string
  value: string
  label: string
}

interface OptionListProps {
  options: Option[]
  selected: string
  onSelect: (value: string) => void
  /** Chips em linha (painel mobile), sem rolagem interna. */
  asChips?: boolean
}

const CHIPS_PREVIEW_LIMIT = 8

function OptionList({ options, selected, onSelect, asChips }: OptionListProps) {
  const [showAll, setShowAll] = useState(false)

  if (asChips) {
    const selectedIndex = options.findIndex(option => option.value === selected)
    const isCollapsed =
      !showAll &&
      options.length > CHIPS_PREVIEW_LIMIT &&
      selectedIndex < CHIPS_PREVIEW_LIMIT
    const visibleOptions = isCollapsed
      ? options.slice(0, CHIPS_PREVIEW_LIMIT)
      : options

    return (
      <ul className="flex flex-wrap gap-2 pb-1">
        {visibleOptions.map(option => {
          const isSelected = option.value === selected
          return (
            <li key={option.key}>
              <button
                aria-pressed={isSelected}
                className={cn(
                  'flex cursor-pointer items-center gap-1.5 rounded-full border px-3.5 py-2 text-left text-sm transition-colors',
                  isSelected
                    ? 'border-primary-600 bg-primary-50 font-semibold text-primary-700'
                    : 'border-gray-200 text-gray-700 active:bg-gray-50'
                )}
                onClick={() => onSelect(isSelected ? '' : option.value)}
                type="button"
              >
                {isSelected && <Check className="size-3.5 shrink-0" />}
                {option.label}
              </button>
            </li>
          )
        })}
        {isCollapsed && (
          <li>
            <button
              className="cursor-pointer rounded-full px-3.5 py-2 font-semibold text-primary-700 text-sm hover:underline"
              onClick={() => setShowAll(true)}
              type="button"
            >
              Ver todas ({options.length})
            </button>
          </li>
        )}
      </ul>
    )
  }

  return (
    <ul className="max-h-72 space-y-0.5 overflow-y-auto pr-1">
      {options.map(option => {
        const isSelected = option.value === selected
        return (
          <li key={option.key}>
            <button
              aria-pressed={isSelected}
              className={cn(
                'flex w-full cursor-pointer items-center justify-between gap-3 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
                isSelected
                  ? 'bg-primary-50 font-semibold text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              )}
              onClick={() => onSelect(isSelected ? '' : option.value)}
              type="button"
            >
              <span className="line-clamp-2">{option.label}</span>
              {isSelected && (
                <Check className="size-4 shrink-0 text-primary-600" />
              )}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

interface AppliedChip {
  key: string
  label: string
  onRemove: () => void
}

interface AppliedFiltersProps {
  chips: AppliedChip[]
  onClear: () => void
}

function AppliedFilters({ chips, onClear }: AppliedFiltersProps) {
  if (chips.length === 0) {
    return null
  }

  return (
    <div className="border-gray-100 border-t py-4">
      <div className="mb-2 flex items-center justify-between">
        <SectionLabel>Filtros aplicados</SectionLabel>
        <button
          className="cursor-pointer font-medium text-primary-700 text-xs hover:underline"
          onClick={onClear}
          type="button"
        >
          Limpar
        </button>
      </div>
      <AppliedChipList chips={chips} />
    </div>
  )
}

function AppliedChipList({ chips }: { chips: AppliedChip[] }) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {chips.map(chip => (
        <li key={chip.key}>
          <button
            aria-label={`Remover filtro ${chip.label}`}
            className="flex cursor-pointer items-center gap-1 rounded-full bg-primary-50 py-1 pr-1.5 pl-2.5 font-medium text-primary-700 text-xs transition-colors hover:bg-primary-100"
            onClick={chip.onRemove}
            type="button"
          >
            {chip.label}
            <X className="size-3.5" />
          </button>
        </li>
      ))}
    </ul>
  )
}

type ChipKey = 'type' | 'area' | 'category' | 'workload'

const CHIP_ORDER: ChipKey[] = ['type', 'area', 'category', 'workload']

function buildAppliedChips({
  labels,
  onRemove,
}: {
  labels: Record<ChipKey, string | undefined>
  onRemove: Record<ChipKey, () => void>
}): AppliedChip[] {
  return CHIP_ORDER.flatMap(key => {
    const label = labels[key]
    return label ? [{ key, label, onRemove: onRemove[key] }] : []
  })
}

interface WorkloadGridProps {
  workloads: string[]
  selected: string
  onSelect: (workload: string) => void
}

function WorkloadGrid({ workloads, selected, onSelect }: WorkloadGridProps) {
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {workloads.map(workload => {
        const isSelected = workload === selected
        return (
          <button
            aria-pressed={isSelected}
            className={cn(
              'cursor-pointer rounded-md py-1.5 font-medium text-xs transition-colors',
              isSelected
                ? 'bg-primary-600 text-white'
                : 'bg-gray-50 text-gray-600 hover:bg-primary-50 hover:text-primary-700'
            )}
            key={workload}
            onClick={() => onSelect(isSelected ? '' : workload)}
            type="button"
          >
            {formatWorkload(workload)}
          </button>
        )
      })}
    </div>
  )
}

/** Categorias da área com o slug real (derivar do nome é só o fallback). */
function useAreaCategories(areaSlug: string) {
  const enabled = Boolean(areaSlug)
  const { data: areaDetail } = useQuery({
    ...areaQueryOptions(areaSlug),
    enabled,
  })
  const { data: categorySlugs } = useQuery({
    ...categorySlugsQueryOptions,
    enabled,
  })

  if (!(enabled && areaDetail)) {
    return []
  }

  return areaDetail.categories.map(category => ({
    ...category,
    slug:
      categorySlugs?.get(category.reference) ?? slugTransform(category.name),
  }))
}

interface ProductsFilterStateInput {
  hasTrack: boolean
  hasPostgraduate: boolean
  workloads?: string[]
}

/** Estado derivado dos filtros, compartilhado pelo aside, painel e barra mobile. */
function useProductsFilterState({
  hasTrack,
  hasPostgraduate,
  workloads = [],
}: ProductsFilterStateInput) {
  const { filters, isPending, updateFilters, clearFilters } =
    useCatalogFilters()

  const { data: allAreas = [] } = useQuery(areasQueryOptions)
  // Trilhas e pós já são escolhidas em "Conteúdo"; não repetem como área.
  const areas = allAreas.filter(area => !isProductTypeSlug(area.url))
  const selectedArea = areas.find(area => area.url === filters.areaSlug)
  const categories = useAreaCategories(filters.areaSlug)
  const selectedCategory = categories.find(
    category => category.slug === filters.categorySlug
  )
  const sortedWorkloads = workloads.toSorted((a, b) => Number(a) - Number(b))

  const contentTypes = getContentTypes({ hasTrack, hasPostgraduate })
  const activeType = contentTypes.find(
    ({ value }) => value === filters.productType
  )

  const chips = buildAppliedChips({
    labels: {
      type:
        filters.productType === DEFAULT_PRODUCT_TYPE ? '' : activeType?.label,
      area: filters.areaSlug && (selectedArea?.name ?? filters.areaSlug),
      category:
        filters.categorySlug &&
        (selectedCategory?.name ?? filters.categorySlug),
      workload: filters.workload && formatWorkload(filters.workload),
    },
    onRemove: {
      type: () => updateFilters({ productType: DEFAULT_PRODUCT_TYPE }),
      area: () => updateFilters({ areaSlug: '' }),
      category: () => updateFilters({ categorySlug: '' }),
      workload: () => updateFilters({ workload: '' }),
    },
  })

  return {
    filters,
    isPending,
    updateFilters,
    clearFilters,
    areas,
    selectedArea,
    categories,
    selectedCategory,
    sortedWorkloads,
    contentTypes,
    chips,
  }
}

/**
 * Filtros aplicados em linha, abaixo da barra mobile. O tipo de conteúdo fica
 * de fora porque já aparece selecionado nos chips da barra.
 */
export function MobileAppliedFilters({
  hasTrack,
  hasPostgraduate,
}: Omit<ProductsFilterStateInput, 'workloads'>) {
  const { chips, clearFilters } = useProductsFilterState({
    hasTrack,
    hasPostgraduate,
  })
  const visibleChips = chips.filter(chip => chip.key !== 'type')

  if (visibleChips.length === 0) {
    return null
  }

  return (
    <div className="flex items-start justify-between gap-3 pt-3">
      <AppliedChipList chips={visibleChips} />
      <button
        className="shrink-0 cursor-pointer py-1 font-medium text-primary-700 text-xs hover:underline"
        onClick={clearFilters}
        type="button"
      >
        Limpar
      </button>
    </div>
  )
}

export interface ProductsFilterProps {
  hasTrack: boolean
  hasPostgraduate: boolean
  workloads: string[]
  className?: string
  /**
   * `sheet`: painel mobile — seções abertas e opções em chips.
   */
  variant?: 'aside' | 'sheet'
}

export function ProductsFilter({
  hasTrack,
  hasPostgraduate,
  workloads,
  className,
  variant = 'aside',
}: ProductsFilterProps) {
  const {
    filters,
    isPending,
    updateFilters,
    clearFilters,
    areas,
    selectedArea,
    categories,
    selectedCategory,
    sortedWorkloads,
    contentTypes,
    chips,
  } = useProductsFilterState({ hasTrack, hasPostgraduate, workloads })
  const isSheet = variant === 'sheet'
  const [openSections, setOpenSections] = useState<string[]>(
    isSheet ? ALL_SECTIONS : []
  )
  const hasCategories = categories.length > 0

  function handleSelectArea(areaSlug: string) {
    updateFilters({ areaSlug })
    if (isSheet) {
      return
    }
    // Fecha "Áreas" e abre os próximos passos: "Categorias" (quando a área
    // tiver alguma) e "Carga horária".
    setOpenSections(areaSlug ? [SECTION.categories, SECTION.workload] : [])
  }

  return (
    <div
      aria-busy={isPending}
      className={cn('transition-opacity', isPending && 'opacity-80', className)}
    >
      {contentTypes.length > 1 && (
        <div className={cn('pb-4', isSheet && 'border-gray-100 border-b pt-4')}>
          <SectionLabel>Conteúdo</SectionLabel>
          <ContentTypeList
            onSelect={productType => updateFilters({ productType })}
            options={contentTypes}
            selected={filters.productType}
          />
        </div>
      )}

      {!isSheet && <AppliedFilters chips={chips} onClear={clearFilters} />}

      <Accordion
        className={cn('border-gray-100', !isSheet && 'border-t')}
        onValueChange={setOpenSections}
        type="multiple"
        value={
          hasCategories
            ? openSections
            : openSections.filter(section => section !== SECTION.categories)
        }
      >
        {areas.length > 0 && (
          <AccordionItem className="border-gray-100" value={SECTION.areas}>
            <FilterTrigger title="Áreas" value={selectedArea?.name} />
            <AccordionContent>
              <OptionList
                asChips={isSheet}
                onSelect={handleSelectArea}
                options={areas.map(area => ({
                  key: area.reference,
                  value: area.url,
                  label: area.name,
                }))}
                selected={filters.areaSlug}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        <AccordionItem
          className="border-gray-100"
          disabled={!hasCategories}
          value={SECTION.categories}
        >
          <FilterTrigger
            placeholder={filters.areaSlug ? undefined : 'Escolha uma área'}
            title="Categorias"
            value={selectedCategory?.name}
          />
          {hasCategories && (
            <AccordionContent>
              <OptionList
                asChips={isSheet}
                onSelect={categorySlug => updateFilters({ categorySlug })}
                options={categories.map(category => ({
                  key: category.reference,
                  value: category.slug,
                  label: category.name,
                }))}
                selected={filters.categorySlug}
              />
            </AccordionContent>
          )}
        </AccordionItem>

        {sortedWorkloads.length > 0 && (
          <AccordionItem className="border-gray-100" value={SECTION.workload}>
            <FilterTrigger
              title="Carga horária"
              value={
                filters.workload ? formatWorkload(filters.workload) : undefined
              }
            />
            <AccordionContent>
              <WorkloadGrid
                onSelect={workload => updateFilters({ workload })}
                selected={filters.workload}
                workloads={sortedWorkloads}
              />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  )
}
